// ============================================
// Xác nhận gửi / rút lại câu trả lời cho khách
// ============================================
// Anh em sinh đôi của `StatusChangeConfirmModal`, cùng khuôn thị giác (overlay
// z-[60], hộp max-w-md, hai nút cùng vị trí) để người dùng nhận ra ngay đây là
// "bước xác nhận" chứ không phải một màn mới. Tách file thay vì nhồi thêm props
// vào hộp kia vì hai bên nói về hai việc khác hẳn nhau: một bên đổi trạng thái
// nội bộ, một bên GỬI CHỮ CHO KHÁCH ĐỌC.
//
// ⚠️ KHI NÀO HỘP NÀY XUẤT HIỆN — quy tắc thông báo của BE, chép lại cho gọn:
//   `store_replied_at == null` ngay TRƯỚC khi gửi  -> lần đầu, BE BẮN thông báo
//   `store_replied_at` đã có giá trị                -> đang sửa, BE IM LẶNG
// Nên `mode='send'` CHỈ được dựng cho ca thứ nhất. Bắt người dùng xác nhận cho
// mỗi lần sửa chính tả là dạy họ bấm "Confirm" theo phản xạ, và đúng lúc hộp này
// thật sự quan trọng thì không ai đọc nữa.
//
// `mode='withdraw'` thì LUÔN có xác nhận, dù BE im lặng: rút lại là gỡ một đoạn
// chữ khách có thể đã đọc, và cái thông báo đã bay đi thì không gọi về được.

import { BellIcon } from '../../../components/icons';
import { excerpt, useFeedbackText } from '../feedbackText';

/** Độ dài trích đoạn BE nhét vào thông báo đẩy — xem `StaffFeedback.store_reply`. */
const NOTIFICATION_EXCERPT_LENGTH = 140;

interface ReplyConfirmModalProps {
  /**
   * `send` = lần trả lời ĐẦU (khách sẽ nhận thông báo).
   * `withdraw` = gỡ câu trả lời đang hiển thị (`store_reply: null`).
   */
  mode: 'send' | 'withdraw';
  /**
   * Chữ SẮP gửi — với `mode='send'` thì đây chính là thứ khách sắp đọc, nên hộp
   * này trích thẳng ra thay vì mô tả chung chung. Bỏ trống ở `mode='withdraw'`.
   */
  replyText?: string;
  isPending: boolean;
  /** Ghi chú nội bộ đang gõ dở — PATCH này không mang nó theo. */
  hasUnsavedNote: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ReplyConfirmModal({
  mode,
  replyText,
  isPending,
  hasUnsavedNote,
  onConfirm,
  onCancel,
}: ReplyConfirmModalProps) {
  const { t, tr } = useFeedbackText();

  const isSend = mode === 'send';
  // Cắt đúng độ dài BE dùng cho thông báo đẩy. Người xử lý nhìn thấy CHÍNH XÁC
  // phần khách đọc được trên màn hình khoá — phần bị cắt cũng là thông tin.
  const preview = isSend ? excerpt(replyText, NOTIFICATION_EXCERPT_LENGTH) : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      // `stopPropagation` BẮT BUỘC: hộp này render BÊN TRONG overlay của
      // `FeedbackDetailModal`, mà overlay đó đóng modal khi bị bấm. Thiếu dòng
      // này thì bấm ra ngoài để huỷ sẽ đóng luôn cả màn chi tiết.
      onClick={(event) => {
        event.stopPropagation();
        if (!isPending) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-slate-900">
          {isSend
            ? tr('feedbackConfirmReplyTitle', 'Send this reply to the customer?')
            : tr('feedbackConfirmWithdrawReplyTitle', 'Withdraw your reply?')}
        </h3>

        {isSend ? (
          <>
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
              <span className="mt-0.5 shrink-0 text-primary-600">
                <BellIcon size={16} />
              </span>
              <p className="text-sm text-slate-700">
                {tr(
                  'feedbackConfirmReplyBody',
                  'This is your first reply on this feedback, so the customer gets a push notification. The notification shows the beginning of your message, translated framing in their own language. It cannot be recalled.'
                )}
              </p>
            </div>

            {preview ? (
              <div className="mt-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">
                  {tr('feedbackConfirmReplyPreviewLabel', 'What the customer will see')}
                </p>
                <div className="mt-1 border-l-2 border-primary-300 pl-3">
                  <p className="whitespace-pre-wrap break-words text-sm text-slate-600">
                    {preview}
                  </p>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-slate-700">
              {tr(
                'feedbackConfirmWithdrawReplyBody',
                'The reply disappears from the customer app right away. But if they already read it, or already received the notification, withdrawing does not take that back.'
              )}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              {tr(
                'feedbackConfirmWithdrawReplyRenotify',
                'Writing a new reply afterwards counts as a first reply again and notifies them a second time.'
              )}
            </p>
          </div>
        )}

        {/* Ghi chú chưa lưu: một PATCH `{ store_reply }` KHÔNG mang theo chữ
            đang gõ dở ở ô ghi chú nội bộ — hai ô, hai request. */}
        {hasUnsavedNote ? (
          <p className="mt-3 text-xs text-amber-600">
            {tr(
              'feedbackConfirmUnsavedNote',
              'Your internal note has not been saved yet and will not be sent with this change. Save it separately.'
            )}
          </p>
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className={
              isSend
                ? 'rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-60'
                : // Rút lại là hành động PHÁ, và không hoàn tác được phần thông
                  // báo — đỏ ở đây là đúng tín hiệu, khác với nút gửi.
                  'rounded-lg bg-[#EF4444] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-60'
            }
          >
            {isPending
              ? t('processing')
              : isSend
              ? tr('feedbackConfirmReplyAction', 'Send and notify')
              : tr('feedbackConfirmWithdrawReplyAction', 'Withdraw reply')}
          </button>
        </div>
      </div>
    </div>
  );
}
